import type { ReactNode } from "react";
import { Settings } from "lucide-react";
import { HOME_BOARD } from "./planBoard";
import type { Cheer } from "./useTaskCounter";

/**
 * PLAN-HOME-1 (s228) — THE PLAN'S FRAME: board 12's header and its two role tabs (STANDING-RULES rule 127, D-217.11;
 * Home.dc.html:60-133). It wraps every screen of the plan, the interview in progress included (Reade, ruling B), so
 * the executor-task counter on the Executor tab shows everywhere and nowhere else (rule 127(2)).
 *
 *  • Header (:62-93): "{name}'s plan" in Fraunces 22px; the plan actions; one Settings gear; Sign out.
 *    🔴 The three plan actions — Save or print, Review, Who can see this — are HIDDEN (Reade, s228: "build the home
 *    first with Save or print, Review and Who can see this HIDDEN"). Their boards (13, 14, 10) are the next block.
 *  • Tabs (:95-133): folder tabs splitting the strip half and half; the tab words and icons are the board's.
 *    Colours: the board's unselected tab #F3EFE7 and ready number #5C7F3F are not brand colours (rule 124) — the
 *    build uses cream and sage 600, reported s228.
 *  • Body: the sidebar (:137-208) beside the page, or the page alone when an interview screen needs the full width.
 */

export type PlanTab = "plan" | "exec";

export function PlanFrame({
  planner,
  tab,
  onTab,
  onSettings,
  settingsOn,
  onSignOut,
  counter,
  sidebar,
  children,
}: {
  /** The planner's first name, for "{name}'s plan". */
  planner: string;
  tab: PlanTab;
  onTab: (tab: PlanTab) => void;
  onSettings: () => void;
  settingsOn: boolean;
  onSignOut: () => void;
  counter: { loaded: boolean; total: number; ready: number; cheer: Cheer };
  sidebar?: ReactNode;
  children: ReactNode;
}) {
  return (
    // No full-screen minimum: on staging the banner above would push every page 38px past the window (measured
    // s228), and "More below" would show on pages that do not continue. The page behind is already cream.
    <div data-interview-theme className="flex w-full flex-col bg-warm-cream font-sans text-warm-charcoal" data-testid="plan-frame">
      <header className="flex min-h-16 shrink-0 items-center justify-between gap-6 bg-warm-white px-4 sm:px-8" data-testid="plan-header">
        <span className="truncate font-serif text-[22px]" data-testid="plan-title">{planner ? `${planner}’s plan` : "Your plan"}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={onSettings} title="Settings" aria-label="Settings" aria-pressed={settingsOn}
            className={`flex h-11 w-11 items-center justify-center rounded-[8px] text-sage-800 hover:bg-warm-stone-light ${settingsOn ? "bg-warm-stone-light" : ""}`}
            data-testid="plan-settings">
            <Settings className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />
          </button>
          <button type="button" onClick={onSignOut}
            className="flex min-h-11 items-center whitespace-nowrap rounded-[8px] px-3 text-[14.5px] text-sage-600 hover:bg-warm-stone-light"
            data-testid="plan-sign-out">
            Sign out
          </button>
        </div>
      </header>

      <div className="flex shrink-0 items-end gap-1.5 border-b border-warm-stone bg-warm-stone-light px-4 pt-3 sm:px-8" role="tablist" data-testid="plan-tabs">
        {HOME_BOARD.tabs.map((t, i) => {
          const id: PlanTab = i === 0 ? "plan" : "exec";
          const on = tab === id;
          return (
            <button key={t.label} type="button" role="tab" aria-selected={on} onClick={() => onTab(id)}
              className={
                // s228 (Reade, item 4): no browser black outline on click; a keyboard focus shows the brand's thin sage
                // ring (the product's focus colour, CELL_BOX). `min-w-0` lets the two tabs share a narrow strip (item 1).
                "-mb-px flex min-h-16 min-w-0 flex-1 basis-0 flex-wrap items-center justify-center gap-3 rounded-t-[12px] border border-t-4 border-warm-stone px-5 " +
                "outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-sage-600 " +
                (on ? "border-t-sage-600 border-b-warm-white bg-warm-white text-sage-800" : "border-t-warm-cream bg-warm-cream text-warm-charcoal")
              }
              data-testid={`plan-tab-${id}`}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={t.icon} />
              </svg>
              <span className="font-serif text-[22px]">{t.label}</span>
              {t.tag && <span className="text-[13px] text-warm-muted">{t.tag}</span>}
              {id === "exec" && <TaskCounter {...counter} />}
            </button>
          );
        })}
      </div>

      <div className="flex min-h-0 flex-1">
        {sidebar}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}

/** The counter on the Executor tab (Home.dc.html:107-128): tasks | ready, with a sparkle or a check, no words. */
function TaskCounter({ loaded, total, ready, cheer }: { loaded: boolean; total: number; ready: number; cheer: Cheer }) {
  if (!loaded) return null;
  return (
    <span className="flex flex-col items-center gap-[3px] rounded-[12px] border-[1.5px] border-warm-stone bg-warm-cream px-3.5 py-1" data-testid="plan-counter">
      <span className="text-[13px] uppercase tracking-[0.08em] text-warm-muted">Executor tasks</span>
      <span className="flex items-center gap-2.5 tabular-nums">
        {/* s233 (board 12 v433): the chip reads ready first, then tasks. */}
        <span className="font-serif text-[20px] text-sage-600" data-testid="plan-counter-ready">{ready}</span>
        <span className="text-[13.5px] text-warm-charcoal">ready</span>
        {cheer === "gone" && (
          <span className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-full bg-sage-600" data-testid="plan-counter-sparkle" aria-hidden="true">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" stroke="none" className="text-warm-white">
              <path d="M11 3l1.9 5.6L18.5 10.5l-5.6 1.9L11 18l-1.9-5.6L3.5 10.5l5.6-1.9z" />
              <path d="M18.5 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
            </svg>
          </span>
        )}
        <span className="h-[18px] w-px bg-warm-stone" aria-hidden="true" />
        <span className="font-serif text-[20px] text-sage-800" data-testid="plan-counter-total">{total}</span>
        <span className="text-[13.5px] text-warm-charcoal">tasks</span>
        {cheer === "ready" && (
          <span className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-full bg-sage-600" data-testid="plan-counter-check" aria-hidden="true">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-warm-white" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          </span>
        )}
      </span>
    </span>
  );
}
